import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { toastr } from "react-redux-toastr";
import ManagePartnersComponent from "./component";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";

const ManagePartnersListContainer = (props) => {
  const navigate = useNavigate();
  const [partnersList, setPartnersList] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const searchTimeoutRef = useRef(null);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((searchTerm) => {
      if (searchTerm.trim()) {
        // Search via API
        fetchPartners(0, searchTerm.trim(), true, true);
      } else {
        // Clear search - load initial partners
        setIsSearching(false);
        if (partnersList.length === 0) {
          fetchPartners(0, "", true);
        } else {
          // Reset to initial state
          fetchPartners(0, "", true);
        }
      }
    }, 500), // 500ms debounce
    []
  );

  const handleSearchInputChange = (event) => {
    const value = event.target.value;
    setSearchValue(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set searching state immediately
    if (value.trim()) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }
    
    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      debouncedSearch(value);
    }, 300);
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setIsSearching(false);
    
    // Clear timeout if exists
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    
    // Reset to initial partners
    fetchPartners(0, "", true);
  };

  const handleSearchSubmit = () => {
    // Optional: You can keep this for manual search trigger
    if (searchValue.trim()) {
      setIsSearching(true);
      fetchPartners(0, searchValue.trim(), true, true);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      // Optional: You can keep this for Enter key search
      e.preventDefault();
      if (searchValue.trim()) {
        setIsSearching(true);
        fetchPartners(0, searchValue.trim(), true, true);
      }
    }
  };

  const handleAddNewEntry = () => {
    navigate("/home/createfranchies");
  };

  const handleLoadMore = () => {
    if (!isLoading && hasMore) {
      const nextPage = pageNumber + 1;
      setPageNumber(nextPage);
      fetchPartners(nextPage, searchValue || "");
    }
  };

  const fetchPartners = useCallback((pageNum, searchTerm = "", reset = false, isSearch = false) => {
    if (isLoading && !isSearch) return;

    setIsLoading(true);
    
    const queryParams = new URLSearchParams({
      pageSize: 20,
      pageNumber: pageNum,
      search: searchTerm || "",
    }).toString();

    props.getDataFromAPI(
      `/dashboard/api/v2/admin/franchisee?${queryParams}`,
      "GET",
      undefined,
      (response) => {
        setIsLoading(false);
        
        if (response && Array.isArray(response)) {
          if (reset) {
            setPartnersList(response);
          } else {
            setPartnersList(prev => [...prev, ...response]);
          }
          
          // Check if there are more results
          setHasMore(response.length === 20);
          setPageNumber(pageNum);
        } else {
          setHasMore(false);
          setPartnersList([]);
        }
      },
      (err) => {
        setIsLoading(false);
        setIsSearching(false);
        toastr.error(`${err.message}`, {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 1000,
        });
      }
    );
  }, [isLoading, props.getDataFromAPI]);

  // Initial load on component mount
  useEffect(() => {
    fetchPartners(0, "", true);
    setIsInitialLoad(false);
  }, []);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleViewDetails = (partnerId, cohortId) => {
    navigate(
      `/home/configuration/managepartnersdetails/${partnerId}/${cohortId}/${partnerId}`
    );
  };

  return (
    <ManagePartnersComponent
      partnersList={partnersList}
      searchValue={searchValue}
      isLoading={isLoading}
      hasMore={hasMore}
      isSearching={isSearching}
      handleSearchInputChange={handleSearchInputChange}
      handleClearSearch={handleClearSearch}
      handleSearchSubmit={handleSearchSubmit}
      handleKeyPress={handleKeyPress}
      handleAddNewEntry={handleAddNewEntry}
      handleLoadMore={handleLoadMore}
      handleViewDetails={handleViewDetails}
    />
  );
};

function mapStateToProps(props) {
  return { props };
}

export default connect(mapStateToProps, { getDataFromAPI })(ManagePartnersListContainer);